import React from 'react';

export enum ChecklistMetricStatus {
  PASSED = 'passed',
  BLOCKED = 'blocked',
  WARNING = 'warning',
  PENDING = 'pending',
  EXCEPTION_NOTED = 'exception_noted',
}

export enum ChecklistBannerVariant {
  SUCCESS = 'success',
  BLOCKED = 'blocked',
  WARNING = 'warning',
  EXCEPTION_NOTED = 'exception_noted',
}

export type CriterionExceptionType = 'dropdown' | 'free_text' | 'none';

export interface ChecklistMetricCriteria {
  description: string;
  status: ChecklistMetricStatus;
  reason?: string;
  message?: string;
  messageNode?: React.ReactNode;
  exception?: string;
  actualValue?: string | number;
  expectedValue?: string | number;
  whereToFix?: {
    label: string;
    url: string;
  };
  exceptionOptions?: string[];
  exceptionType?: CriterionExceptionType;
}

export interface MultiLocationResult {
  locationId: string;
  locationName?: string;
  criterias: ChecklistMetricCriteria[];
}

export interface ExceptionChangeParams {
  metricId: string;
  locationId: string;
  criteriaIndex: number;
  exception: string;
}

export type ChecklistMetricItem = {
  id: string;
  name: string;
  status: ChecklistMetricStatus;
  isRequired: boolean;
  isLoading?: boolean;
  allowExceptions?: boolean;
  criteria: ChecklistMetricCriteria[];
};

export type MultiLocationChecklistMetricItem = Omit<ChecklistMetricItem, 'criteria'> & {
  locationResults: MultiLocationResult[];
};

export type MetricItemData = ChecklistMetricItem | MultiLocationChecklistMetricItem;

export type ChecklistActiveTab = 'all' | 'blocked';

export interface BaseCriteriaResult {
  status: ChecklistMetricStatus;
  message?: string;
  messageNode?: React.ReactNode;
  actualValue?: string | number;
  expectedValue?: string | number;
}

export interface BaseCriteriaDefinition<
  TContext,
  TResult extends BaseCriteriaResult = BaseCriteriaResult
> {
  description: string;
  whereToFix?: {
    label: string;
    url: string | ((ctx: TContext) => string);
  };
  evaluate: (ctx: TContext) => Promise<TResult>;
  exceptionOptions?: string[];
  exceptionType?: CriterionExceptionType;
  shouldSkip?: (ctx: TContext) => boolean;
}

export interface BaseMetricDefinition<TCriteria> {
  id: string;
  name: string;
  isRequired: boolean;
  allowExceptions?: boolean;
  criteria: TCriteria[];
}
